import axios, { type AxiosRequestConfig } from 'axios';
import type { BoundingBox } from '@/types/BoundingBox.types';
import type { Product, ProductReviewData } from '@/types/Product.types';
import slaydApi from './axiosInstance';

export interface HomePageProductListData {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasNext: boolean;
  hasPrevious: boolean;
  results: Product[];
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

export interface SimilarProductsAPIResponse {
  products: Product[];
  pagination: PaginationInfo;
  quickFilters?: string[]; // Added quickFilters
}

export interface SimilarProductsParams {
  page?: number;
  minPrice?: number;
  maxPrice?: number;
}

export interface SimilarProductsImageParams extends SimilarProductsParams {
  imageUrl: string;
  searchType?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  sale?: boolean;
}

export interface SimillarProductsTextParams extends SimilarProductsParams {
  text: string;
  sale?: boolean;
}

export const fetchSimilarProducts = async (
  params: SimilarProductsImageParams,
  config?: AxiosRequestConfig
): Promise<SimilarProductsAPIResponse> => {
  try {
    const response = await slaydApi.get<SimilarProductsAPIResponse>(
      `/api/similar-products/`,
      {
        params: {
          image_url: params.imageUrl,
          search_type: params.searchType || 'combined_75',
          page: params.page || 1,
          x: params.x,
          y: params.y,
          width: params.width,
          height: params.height,
          min_price: params.minPrice,
          max_price: params.maxPrice,
          ...(params.sale ? { sale: true } : {}),
        },
        ...config,
      }
    );

    if (!response?.data?.products) {
      throw new Error('Invalid Response');
    }

    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }
    console.error(
      `Error fetching similar products for ${params.imageUrl}: \n`,
      error
    );
    throw new Error(`Failed fetching similar products`);
  }
};

export const fetchSimilarProductsWithBox = async ({
  imageUrl,
  pageNum = 1,
  box,
  minPrice,
  maxPrice,
  sale,
  config,
}: {
  imageUrl: string;
  pageNum?: number;
  box?: BoundingBox;
  minPrice?: number;
  maxPrice?: number;
  sale?: boolean;
  config?: AxiosRequestConfig;
}): Promise<Product[]> => {
  const response = await fetchSimilarProducts(
    {
      imageUrl: imageUrl,
      page: pageNum,
      x: box?.x,
      y: box?.y,
      width: box?.width,
      height: box?.height,
      minPrice: minPrice,
      maxPrice: maxPrice,
      sale: sale,
    },
    config
  );

  return response.products;
};

/**
 * Fetch a product's data by its ID.
 * The API returns an array, so we return the first item.
 */
export async function fetchProductDataById(id: string): Promise<Product> {
  try {
    const response = await slaydApi.get<Product[]>('/api/myntra/products/', {
      params: { id },
    });

    if (!response.data || !response.data.length)
      throw new Error('Invalid response');

    return response.data[0];
  } catch (error) {
    console.error(error);
    throw new Error(`Error fetching product data`);
  }
}

export const fetchSimilarProductsByText = async (
  params: SimillarProductsTextParams,
  config?: AxiosRequestConfig
): Promise<SimilarProductsAPIResponse> => {
  try {
    const response = await slaydApi.get<SimilarProductsAPIResponse>(
      `/api/text-search/`,
      {
        params: {
          text: params.text,
          min_price: params.minPrice,
          max_price: params.maxPrice,
          page: params.page || 1,
          ...(params.page === 1 ? { get_quick_filters: true } : {}), // Only add when page=1
          ...(params.sale ? { sale: true } : {}),
        },
        ...config,
      }
    );

    if (!response?.data?.products) {
      throw new Error('Invalid Response');
    }

    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }
    console.error(
      `Error fetching similar products for ${params.text}: \n`,
      error
    );
    throw new Error(`Failed fetching similar products`);
  }
};

export const getHomePageInfiniteProducts = async (
  pageNum: number
): Promise<HomePageProductListData> => {
  try {
    const response = await slaydApi.get<HomePageProductListData>(
      `/api/homepage/`,
      {
        params: {
          page: pageNum,
        },
      }
    );

    if (!response?.data?.results) {
      throw new Error('Invalid Response');
    }

    return response.data;
  } catch (error) {
    console.error(`Error fetching products list for homepage: \n`, error);
    throw new Error(`Failed fetching products list for homepage`);
  }
};

export interface GenerateSimilarProductsFeedParams {
  imageUrls: string[];
  minPrice?: number;
  maxPrice?: number;
  sale?: boolean;
  brands?: string[];
  marketplaces?: string[];
  page?: number;
}

export interface GenerateSimilarProductsFeedResponse {
  products: Product[];
  message?: string;
  pagination?: PaginationInfo;
}

export interface InternalCurationFilterOption {
  id: string;
  label: string;
  value: string;
}

interface FiltersFacetOption {
  id: string;
  label: string;
  value?: string;
  type?: string;
}

interface FiltersFacet {
  id: string;
  label: string;
  type: string;
  param: string;
  options?: FiltersFacetOption[];
}

interface CategoryFiltersAPIResponse {
  facets?: FiltersFacet[];
}

/**
 * Generate a product feed from multiple uploaded image URLs
 */
export const generateSimilarProductsFeed = async (
  params: GenerateSimilarProductsFeedParams
): Promise<GenerateSimilarProductsFeedResponse> => {
  try {
    const requestBody: Record<string, unknown> = {
      image_urls: params.imageUrls,
    };

    if (params.minPrice !== undefined) requestBody.min_price = params.minPrice;
    if (params.maxPrice !== undefined) requestBody.max_price = params.maxPrice;
    if (params.sale !== undefined) requestBody.sale = params.sale;
    if (params.brands?.length) requestBody.brands = params.brands;
    if (params.marketplaces?.length)
      requestBody.marketplaces = params.marketplaces;
    if (params.page !== undefined) requestBody.page = params.page;

    const response = await slaydApi.post<GenerateSimilarProductsFeedResponse>(
      '/api/similar-products/generate-feed/',
      requestBody
    );

    if (!response?.data?.products) {
      throw new Error('Invalid Response');
    }

    return response.data;
  } catch (error) {
    console.error('Error generating similar products feed:', error);
    throw new Error('Failed to generate similar products feed');
  }
};

export interface ProductDataForPost {
  product_link: string;
  name?: string;
  price?: string;
  discount_price?: string;
  brand?: string;
  color?: string;
  category?: string;
  subcategory?: string;
  gender?: string;
  marketplace?: string;
  image_url?: string;
  secondary_images?: string;
  available_sizes?: string;
  description?: string;
  [key: string]: string | undefined;
}

export const fetchInternalCurationFilters = async (): Promise<{
  brands?: {
    id: string;
    label: string;
    type: string;
    param: string;
    searchable: boolean;
    options: InternalCurationFilterOption[];
  };
  marketplaces?: {
    id: string;
    label: string;
    type: string;
    param: string;
    searchable: boolean;
    options: InternalCurationFilterOption[];
  };
}> => {
  try {
    const response = await slaydApi.get<CategoryFiltersAPIResponse>(
      '/api/filters/',
      { params: { context: 'category' } }
    );
    const brandFacet = response.data.facets?.find(
      (facet) => facet.id === 'brand'
    );
    const options = brandFacet?.options || [];

    const brands = options
      .filter((option) => option.type === 'Brand')
      .map((option) => ({
        id: option.id,
        label: option.label,
        // Use the API id as requested for brand selection payloads.
        value: option.id,
      }));

    const marketplaces = options
      .filter((option) => option.type === 'Marketplace')
      .map((option) => ({
        id: option.id,
        label: option.label,
        value: option.id,
      }));

    return {
      brands: {
        id: 'brands',
        label: 'Brands',
        type: 'multi_select',
        param: 'brands',
        searchable: true,
        options: brands,
      },
      marketplaces: {
        id: 'marketplaces',
        label: 'Marketplaces',
        type: 'multi_select',
        param: 'marketplaces',
        searchable: false,
        options: marketplaces,
      },
    };
  } catch (error) {
    console.error('Error fetching internal curation filters:', error);
    return {};
  }
};

export async function createProduct(
  productData: ProductDataForPost
): Promise<Product> {
  try {
    const { product_link, ...otherFields } = productData;

    if (!product_link) {
      throw new Error('product_link is required');
    }

    const requestBody: Record<string, unknown> = {
      product_link: product_link,
    };

    Object.entries(otherFields).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        requestBody[key] = value;
      }
    });

    const response = await slaydApi.post<Product | Product[]>(
      '/api/myntra/products/',
      requestBody
    );

    const product = Array.isArray(response.data)
      ? response.data[0]
      : response.data;

    if (!product) {
      throw new Error('Invalid response');
    }

    return product;
  } catch (error) {
    console.error('Error creating product from link:', error);
    throw new Error('Failed to create product from link');
  }
}

/**
 * Fetch additional info like reviews for a product
 */
export async function fetchProductAdditionalInfo(params: {
  id: string;
}): Promise<ProductReviewData> {
  try {
    const response = await slaydApi.get<ProductReviewData>(
      '/api/myntra/products/additional-info/',
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching additional product info:', error);
    throw new Error('Failed to fetch additional product info');
  }
}
